import React, { useState } from 'react';
import { XIcon } from 'lucide-react';
interface RoomQuickSetupModalProps {
  onClose: () => void;
  onApply: (roomData: any) => void;
}
export function RoomQuickSetupModal({
  onClose,
  onApply
}: RoomQuickSetupModalProps) {
  const [width, setWidth] = useState(400);
  const [length, setLength] = useState(350);
  const [selectedLayout, setSelectedLayout] = useState<string | null>(null);
  const layouts = [{
    id: 'island',
    name: 'Kitchen with Island',
    description: 'Suitable for large spaces',
    icon: <svg viewBox="0 0 100 100" className="w-16 h-16 mx-auto" data-id="element-774">
          <rect x="10" y="10" width="80" height="20" fill="white" stroke="currentColor" strokeWidth="2" data-id="element-775" />
          <rect x="10" y="70" width="80" height="20" fill="white" stroke="currentColor" strokeWidth="2" data-id="element-776" />
          <rect x="30" y="40" width="40" height="20" fill="white" stroke="currentColor" strokeWidth="2" data-id="element-777" />
        </svg>
  }, {
    id: 'linear',
    name: 'Linear Kitchen',
    description: 'Suitable for narrow spaces',
    icon: <svg viewBox="0 0 100 100" className="w-16 h-16 mx-auto" data-id="element-778">
          <rect x="10" y="40" width="80" height="20" fill="white" stroke="currentColor" strokeWidth="2" data-id="element-779" />
        </svg>
  }, {
    id: 'u-shaped',
    name: 'U-shaped Kitchen',
    description: 'Suitable for large spaces',
    icon: <svg viewBox="0 0 100 100" className="w-16 h-16 mx-auto" data-id="element-780">
          <rect x="10" y="20" width="20" height="60" fill="white" stroke="currentColor" strokeWidth="2" data-id="element-781" />
          <rect x="30" y="70" width="40" height="20" fill="white" stroke="currentColor" strokeWidth="2" data-id="element-782" />
          <rect x="70" y="20" width="20" height="60" fill="white" stroke="currentColor" strokeWidth="2" data-id="element-783" />
        </svg>
  }, {
    id: 'l-shaped',
    name: 'L-shaped Kitchen',
    description: 'Suitable for corner spaces',
    icon: <svg viewBox="0 0 100 100" className="w-16 h-16 mx-auto" data-id="element-784">
          <rect x="10" y="40" width="60" height="20" fill="white" stroke="currentColor" strokeWidth="2" data-id="element-785" />
          <rect x="70" y="40" width="20" height="50" fill="white" stroke="currentColor" strokeWidth="2" data-id="element-786" />
        </svg>
  }];
  const handleApply = () => {
    // Create room data based on selected dimensions and layout
    const roomData = {
      dimensions: {
        width,
        length
      },
      layout: selectedLayout,
      walls: createWallsFromDimensions(width, length, selectedLayout)
    };
    onApply(roomData);
  };
  // Create walls based on dimensions and selected layout
  const createWallsFromDimensions = (width: number, length: number, layout: string | null) => {
    const centerX = 400;
    const centerY = 300;
    const widthPx = width / 2;
    const lengthPx = length / 2;
    // Basic rectangular room
    const walls = [{
      start: {
        x: centerX - widthPx,
        y: centerY - lengthPx
      },
      end: {
        x: centerX + widthPx,
        y: centerY - lengthPx
      },
      length: width
    }, {
      start: {
        x: centerX + widthPx,
        y: centerY - lengthPx
      },
      end: {
        x: centerX + widthPx,
        y: centerY + lengthPx
      },
      length: length
    }, {
      start: {
        x: centerX + widthPx,
        y: centerY + lengthPx
      },
      end: {
        x: centerX - widthPx,
        y: centerY + lengthPx
      },
      length: width
    }, {
      start: {
        x: centerX - widthPx,
        y: centerY + lengthPx
      },
      end: {
        x: centerX - widthPx,
        y: centerY - lengthPx
      },
      length: length
    }];
    return walls;
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-787">
      <div className="bg-gray-900 rounded-lg shadow-xl w-full max-w-lg text-white" data-id="element-788">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b border-gray-700" data-id="element-789">
          <h2 className="text-xl font-bold" data-id="element-790">Room Settings and Layout</h2>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-800" data-id="element-791">
            <XIcon className="h-5 w-5" data-id="element-792" />
          </button>
        </div>
        <div className="p-5" data-id="element-793">
          {/* Room dimensions */}
          <div className="mb-6" data-id="element-794">
            <h3 className="text-lg font-medium mb-3" data-id="element-795">Room Dimensions</h3>
            <div className="flex gap-4" data-id="element-796">
              <div className="flex-1" data-id="element-797">
                <label className="block text-gray-400 text-sm mb-1" data-id="element-798">
                  Width (cm)
                </label>
                <input type="number" value={width} onChange={e => setWidth(Number(e.target.value))} className="w-full bg-gray-800 border border-gray-700 rounded p-2 text-white text-center" data-id="element-799" />
              </div>
              <div className="flex-1" data-id="element-800">
                <label className="block text-gray-400 text-sm mb-1" data-id="element-801">
                  Length (cm)
                </label>
                <input type="number" value={length} onChange={e => setLength(Number(e.target.value))} className="w-full bg-gray-800 border border-gray-700 rounded p-2 text-white text-center" data-id="element-802" />
              </div>
            </div>
          </div>
          {/* Layout selection */}
          <div className="mb-6" data-id="element-803">
            <h3 className="text-lg font-medium mb-3" data-id="element-804">
              Choose Recommended Layout
            </h3>
            <div className="grid grid-cols-2 gap-4" data-id="element-805">
              {layouts.map(layout => <div key={layout.id} onClick={() => setSelectedLayout(layout.id)} className={`
                    border rounded-lg p-3 text-center cursor-pointer transition-colors
                    ${selectedLayout === layout.id ? 'border-blue-500 bg-blue-900 bg-opacity-20' : 'border-gray-700 hover:border-gray-500'}
                  `} data-id="element-806">
                  <div className="text-gray-300" data-id="element-807">{layout.icon}</div>
                  <h4 className="font-medium mt-2" data-id="element-808">{layout.name}</h4>
                  <p className="text-xs text-gray-400 mt-1" data-id="element-809">
                    {layout.description}
                  </p>
                </div>)}
            </div>
          </div>
          {/* Buttons */}
          <div className="flex gap-3 mt-6" data-id="element-810">
            <button onClick={onClose} className="flex-1 py-3 bg-gray-800 hover:bg-gray-700 rounded-md transition-colors" data-id="element-811">
              Manual Design
            </button>
            <button onClick={handleApply} className="flex-1 py-3 bg-blue-600 hover:bg-blue-500 rounded-md transition-colors" disabled={!selectedLayout} data-id="element-812">
              Start Design
            </button>
          </div>
        </div>
      </div>
    </div>;
}